import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, useApi, useRefresh } from "@/lib/api";
import { fmtDate } from "@/lib/crm";

const STATUS = { aperta: "Aperta", evasa: "Evasa", respinta: "Respinta" };
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { type: "accesso", requester: "", channel: "", received_at: today(), notes: "", status: "aperta", response: "" };

const DsrForm = ({ item, types, onClose }) => {
  const [f, setF] = useState(item || EMPTY);
  const refresh = useRefresh();
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    try {
      const body = { type: f.type, requester: f.requester, channel: f.channel, received_at: f.received_at, notes: f.notes, status: f.status, response: f.response, client_id: f.client_id || null };
      if (item) await api.put(`/dsr/${item.dsr_id}`, body); else await api.post("/dsr", body);
      toast.success("Richiesta salvata"); refresh(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3">
      <Select value={f.type} onValueChange={set("type")}><SelectTrigger data-testid="dsr-type-select"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(types).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
      <Input data-testid="dsr-requester-input" placeholder="Nome di chi fa la richiesta" value={f.requester} onChange={set("requester")} />
      <div className="grid grid-cols-2 gap-2">
        <Input data-testid="dsr-received-input" type="date" value={f.received_at} onChange={set("received_at")} />
        <Input data-testid="dsr-channel-input" placeholder="Canale (email, PEC…)" value={f.channel} onChange={set("channel")} />
      </div>
      <Textarea data-testid="dsr-notes-input" rows={2} placeholder="Dettagli della richiesta" value={f.notes} onChange={set("notes")} />
      <Select value={f.status} onValueChange={set("status")}><SelectTrigger data-testid="dsr-status-select"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
      <Textarea data-testid="dsr-response-input" rows={2} placeholder="Risposta data / azioni eseguite" value={f.response} onChange={set("response")} />
      <Button data-testid="dsr-save-btn" disabled={f.requester.trim().length < 2} onClick={save} className="w-full bg-[#0F172A] hover:bg-sky-600">Salva</Button>
    </div>
  );
};

export const DsrRegistry = () => {
  const { data } = useApi("/dsr");
  const refresh = useRefresh();
  const [edit, setEdit] = useState(null);
  const remove = async (id) => { try { await api.delete(`/dsr/${id}`); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const late = (x) => x.status === "aperta" && x.due_at < today();
  return (
    <section className="bg-white border border-slate-200/80 rounded-xl p-6 space-y-4" data-testid="dsr-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display font-semibold">Registro richieste privacy dei clienti</h3>
          <p className="text-sm text-slate-500 mt-1">Annota le richieste di accesso, cancellazione, revoca, ecc. Va data risposta entro 30 giorni (art. 12 GDPR).</p>
        </div>
        <Button data-testid="dsr-add-btn" variant="outline" onClick={() => setEdit({})}><Plus className="h-4 w-4 mr-1" />Nuova</Button>
      </div>
      <div className="divide-y divide-slate-100">
        {data?.items.length === 0 && <p className="text-sm text-slate-400 py-3">Nessuna richiesta registrata.</p>}
        {data?.items.map((x) => (
          <div key={x.dsr_id} className="py-3 flex items-center justify-between gap-3" data-testid={`dsr-row-${x.dsr_id}`}>
            <button className="text-left flex-1" onClick={() => setEdit(x)}>
              <p className="text-sm font-medium">{x.requester} · {data.types[x.type]}</p>
              <p className={`text-xs ${late(x) ? "text-rose-600" : "text-slate-500"}`}>Ricevuta {fmtDate(x.received_at)} · scadenza {fmtDate(x.due_at)} · {STATUS[x.status]}{late(x) ? " · IN RITARDO" : ""}</p>
            </button>
            <button data-testid={`dsr-delete-${x.dsr_id}`} onClick={() => remove(x.dsr_id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{edit?.dsr_id ? "Modifica richiesta" : "Nuova richiesta privacy"}</DialogTitle></DialogHeader>
          {edit && data && <DsrForm item={edit.dsr_id ? edit : null} types={data.types} onClose={() => setEdit(null)} />}
        </DialogContent>
      </Dialog>
    </section>
  );
};
